import { useState, useEffect } from 'react';
import { X, ArrowDownLeft, ArrowUpRight, Coins } from 'lucide-react';
import { getTransactions } from '../../api/credits';
import type { CreditTransaction } from '../../api/credits';

interface ActivityPanelProps {
  onClose: () => void;
}

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function ActivityPanel({ onClose }: ActivityPanelProps) {
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getTransactions(20).then((res) => {
      if (res.success) setTransactions(res.data);
      setLoading(false);
    });
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-yellow-400" />
            <h2 className="text-lg font-bold">Recent Activity</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-800 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="text-center py-8 text-slate-400">Loading...</div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8 text-slate-400">No activity yet.</div>
          ) : (
            <div className="space-y-2">
              {transactions.map((tx) => {
                const isAward = tx.amount > 0;
                return (
                  <div
                    key={tx.transaction_id}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-slate-800/40"
                  >
                    <div className={`p-1.5 rounded-lg ${isAward ? 'bg-green-900/50' : 'bg-red-900/50'}`}>
                      {isAward ? (
                        <ArrowDownLeft className="w-4 h-4 text-green-400" />
                      ) : (
                        <ArrowUpRight className="w-4 h-4 text-red-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{tx.reason}</div>
                      <div className="text-xs text-slate-500">{formatTimeAgo(tx.created_at)}</div>
                    </div>
                    <div className={`text-sm font-mono font-semibold ${isAward ? 'text-green-400' : 'text-red-400'}`}>
                      {isAward ? '+' : ''}{tx.amount}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
